import type {
  Configuration,
  CreateSnapshotRecipeRequest,
  ListSnapshotRecipeEntriesResponse,
  ListSnapshotRecipesResponse,
  SnapshotRecipe,
  SnapshotRecipeEntry,
  UpdateSnapshotRecipeRequest,
} from '@repo/api-gw-sdk';
import { SnapshotRecipeStatus, SnapshotRecipesApi } from '@repo/api-gw-sdk';

import type { HttpClient } from './httpClient';

const baseUrl = '/snapshot-recipes';
const RESOLVING_REFRESH_INTERVAL_MS = 5_000;
// The API caps a page at 200 recipes and 1000 entries.
const RECIPES_PAGE_SIZE = 200;
const ENTRIES_PAGE_SIZE = 1000;
// A resource has few recipes, so the list reads every page; the cap only guards against a token that never ends.
const MAX_RECIPE_PAGES = 50;
// Later entry pages are read from the same query's results, so they're cheap. Past the cap a directory reports a
// nextPageToken and its unread rows show as not checked.
const MAX_ENTRY_PAGES = 10;

const isResolving = (status: SnapshotRecipeStatus | undefined) =>
  status === SnapshotRecipeStatus.SNAPSHOT_RECIPE_STATUS_RESOLVING ||
  status === SnapshotRecipeStatus.SNAPSHOT_RECIPE_STATUS_DRAFT;

export const snapshotRecipes = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const api = new SnapshotRecipesApi(config);

  const listAllRecipes = async (
    projectId: string,
    resourceId: string
  ): Promise<ListSnapshotRecipesResponse> => {
    const recipes: SnapshotRecipe[] = [];
    let pageToken: string | undefined;
    for (let page = 0; page < MAX_RECIPE_PAGES; page++) {
      const res = await api.listSnapshotRecipes(
        projectId,
        resourceId,
        pageToken,
        RECIPES_PAGE_SIZE
      );
      recipes.push(...(res.recipes ?? []));
      pageToken = res.nextPageToken || undefined;
      if (!pageToken) {
        break;
      }
    }
    return { recipes, nextPageToken: pageToken };
  };

  const listEntries = async (
    projectId: string,
    resourceId: string,
    recipeId: string,
    path: string
  ): Promise<ListSnapshotRecipeEntriesResponse> => {
    const entries: SnapshotRecipeEntry[] = [];
    let pageToken: string | undefined;
    for (let page = 0; page < MAX_ENTRY_PAGES; page++) {
      const res = await api.listSnapshotRecipeEntries(
        projectId,
        resourceId,
        recipeId,
        pageToken,
        ENTRIES_PAGE_SIZE,
        { path }
      );
      entries.push(...(res.entries ?? []));
      pageToken = res.nextPageToken || undefined;
      if (!pageToken) {
        break;
      }
    }
    return { entries, nextPageToken: pageToken };
  };

  return {
    useList: (resourceId: string | undefined) =>
      httpClient.execute(
        listAllRecipes,
        resourceId ? baseUrl : null,
        'list',
        currentProjectId,
        resourceId ?? ''
      ),
    useGet: (resourceId: string, recipeId: string | undefined) =>
      httpClient.executeWithConfig(
        api.getSnapshotRecipe.bind(api),
        recipeId ? baseUrl : null,
        'get',
        {
          // A missing or forbidden recipe must surface as an error, but a transient failure has to retry: SWR's
          // refreshInterval skips revalidation while the cache holds an error, so a RESOLVING recipe would freeze.
          shouldRetryOnError: (err) =>
            ![403, 404].includes((err as { code?: number }).code ?? 0),
          // A reconnect revalidation can load a new revision, which remounts the workspace and drops a dirty draft.
          revalidateOnReconnect: false,
          refreshInterval: (data) =>
            isResolving(data?.recipe?.status)
              ? RESOLVING_REFRESH_INTERVAL_MS
              : 0,
        },
        currentProjectId,
        resourceId,
        recipeId ?? ''
      ),
    useEntries: (
      resourceId: string,
      recipeId: string | undefined,
      path: string,
      // Only keys the cache, so a re-resolved revision never shows the previous revision's entries.
      revision?: number
    ) =>
      httpClient.execute(
        listEntries,
        recipeId ? baseUrl : null,
        `entries:${revision ?? 0}`,
        currentProjectId,
        resourceId,
        recipeId ?? '',
        path
      ),
    get: (resourceId: string, recipeId: string) =>
      api.getSnapshotRecipe(currentProjectId, resourceId, recipeId),
    create: (resourceId: string, body: CreateSnapshotRecipeRequest) =>
      api.createSnapshotRecipe(currentProjectId, resourceId, body),
    update: (
      resourceId: string,
      recipeId: string,
      body: UpdateSnapshotRecipeRequest
    ) => api.updateSnapshotRecipe(currentProjectId, resourceId, recipeId, body),
    delete: (resourceId: string, recipeId: string) =>
      api.deleteSnapshotRecipe(currentProjectId, resourceId, recipeId),
  };
};
